import React, { useEffect, useRef, useState } from 'react';
import { createChibiRig, ALL_MOODS } from '../components/chibi/chibi-rig';

// Chibi 2.0 art-review page (dev-only route, /chibi-preview).
//
// Two panels:
//   • Stage — one big chibi + a mood button bar, for judging transition
//     animations, particles and idle bits (the "does he feel alive" test).
//     Optional auto-tour cycles every mood at 2.2s for hands-free review.
//   • Grid — every mood side by side at rail-card scale, for judging
//     consistency + readability at a glance.
//
// Deliberately zero app dependencies beyond the rig, so it renders even if
// backends are down — this page is for looking at the crab.

const PAGE_BG = 'linear-gradient(180deg, #1b1526 0%, #241a33 60%, #1d1428 100%)';
const CARD_BG = 'linear-gradient(180deg, #2a1f3d, #221833)';
const EXPLORER_ACCENT = '#bf94ff';

function RigBox({ mood, size = 120, onMount }) {
  const ref = useRef(null);
  const rigRef = useRef(null);
  useEffect(() => {
    if (!ref.current) return undefined;
    rigRef.current = createChibiRig(ref.current, { mood });
    if (onMount) onMount(rigRef.current);
    return () => rigRef.current?.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { rigRef.current?.setMood(mood); }, [mood]);
  return <div ref={ref} style={{ width: size, height: size }} />;
}

function ChibiPreviewPage() {
  const [stageMood, setStageMood] = useState('content');
  const [autoTour, setAutoTour] = useState(false);
  const [gridDark, setGridDark] = useState(true);
  const stageRigRef = useRef(null);

  useEffect(() => {
    if (!autoTour) return undefined;
    const id = setInterval(() => {
      setStageMood((m) => ALL_MOODS[(ALL_MOODS.indexOf(m) + 1) % ALL_MOODS.length]);
    }, 2200);
    return () => clearInterval(id);
  }, [autoTour]);

  return (
    <div style={{ minHeight: '100vh', background: PAGE_BG, color: '#ede7ff', fontFamily: 'monospace', padding: '24px 20px 60px' }}>
      <h1 style={{ fontSize: 20, margin: '0 0 4px' }}>🦀 Chibi 2.0 preview</h1>
      <p style={{ margin: '0 0 20px', opacity: 0.65, fontSize: 12 }}>
        layered SVG rig · {ALL_MOODS.length} moods · click a mood to watch the transition · he blinks, drifts his gaze and does idle bits on his own
      </p>

      {/* ── Stage ── */}
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: 34 }}>
        <div
          data-testid="chibi-stage"
          style={{ width: 340, height: 340, borderRadius: 18, background: gridDark ? CARD_BG : '#f3ecff', border: `2px solid ${EXPLORER_ACCENT}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
        >
          <RigBox mood={stageMood} size={280} onMount={(rig) => { stageRigRef.current = rig; }} />
        </div>
        <div style={{ flex: 1, minWidth: 280 }}>
          <div style={{ marginBottom: 10, fontSize: 13 }}>
            current: <b style={{ color: EXPLORER_ACCENT }} data-testid="stage-mood">{stageMood}</b>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
            {ALL_MOODS.map((m) => (
              <button
                key={m}
                type="button"
                data-testid={`mood-btn-${m}`}
                onClick={() => setStageMood(m)}
                style={{
                  padding: '5px 9px', fontSize: 11, fontFamily: 'monospace', cursor: 'pointer', borderRadius: 7,
                  border: `1px solid ${m === stageMood ? EXPLORER_ACCENT : '#4a3a63'}`,
                  background: m === stageMood ? `${EXPLORER_ACCENT}2b` : '#2a2040',
                  color: m === stageMood ? '#fff' : '#c9bce4',
                }}
              >
                {m}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" data-testid="auto-tour" onClick={() => setAutoTour((v) => !v)} style={{ padding: '6px 12px', fontFamily: 'monospace', fontSize: 12, borderRadius: 7, cursor: 'pointer', border: `1px solid ${EXPLORER_ACCENT}`, background: autoTour ? EXPLORER_ACCENT : 'transparent', color: autoTour ? '#1b1526' : EXPLORER_ACCENT }}>
              {autoTour ? '⏸ stop tour' : '▶ auto-tour all moods'}
            </button>
            <button type="button" data-testid="idle-bit" onClick={() => stageRigRef.current?.triggerIdleBit()} style={{ padding: '6px 12px', fontFamily: 'monospace', fontSize: 12, borderRadius: 7, cursor: 'pointer', border: '1px solid #4a3a63', background: 'transparent', color: '#c9bce4' }}>
              🎲 random idle bit
            </button>
            <button type="button" onClick={() => setGridDark((v) => !v)} style={{ padding: '6px 12px', fontFamily: 'monospace', fontSize: 12, borderRadius: 7, cursor: 'pointer', border: '1px solid #4a3a63', background: 'transparent', color: '#c9bce4' }}>
              {gridDark ? '☀️ light bg' : '🌙 dark bg'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Grid ── */}
      <h2 style={{ fontSize: 14, margin: '0 0 12px', opacity: 0.8 }}>all moods at rail-card scale</h2>
      <div data-testid="chibi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
        {ALL_MOODS.map((m) => (
          <div
            key={m}
            data-testid={`grid-${m}`}
            style={{ background: gridDark ? CARD_BG : '#f3ecff', border: `1px solid ${EXPLORER_ACCENT}22`, borderRadius: 12, padding: '14px 8px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}
          >
            <RigBox mood={m} size={110} />
            <span style={{ fontSize: 11, color: gridDark ? '#c9bce4' : '#5c4a7a' }}>{m}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ChibiPreviewPage;
